import { memo, useMemo } from 'react';
import { Version, SelectOption } from 'models';
import { useTranslation } from 'react-i18next';

import { BalanceType } from 'utils/enums';
import { balanceTypesOptions } from 'configs/balances-report';

import { HTMLParser, PageTitle } from 'containers';
import {
  FiltersBar,
  PageLoader,
  Error,
  Download,
  DatePicker,
  ControlWrapper,
  Select,
} from 'components';

import { useStyles } from './style';

interface Props {
  balanceType: SelectOption<BalanceType>;
  fileName: string;
  startDate: Date | null;
  endDate: Date | null;
  version: Version;
  url: string;
  loading: boolean;
  error: boolean;
  handleChangeDate: (date: Date | null, name?: string) => void;
  handleChangeVersion: (version: Version) => void;
  handleChangeBalanceType: (option: SelectOption<BalanceType>) => void;
}

const versions: Version[] = [
  { label: '#portal.investor.report.balance.list.expand', value: 0 },
  { label: '#portal.investor.report.balance.list.collapse', value: 1 },
];

const InvestotrBalanceDetails: React.FC<Props> = ({
  balanceType,
  startDate,
  endDate,
  fileName,
  version,
  url,
  loading,
  error,
  handleChangeDate,
  handleChangeVersion,
  handleChangeBalanceType,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const selectCls = useMemo(
    () => ({
      value: classes.value,
      label: classes.label,
      btn: {
        root: classes.btn,
      },
    }),
    [classes]
  );

  if (error) return <Error />;
  if (loading) return <PageLoader />;

  return (
    <>
      <PageTitle />
      <FiltersBar className={classes.filtersBar}>
        <ControlWrapper className={classes.controlWrapper}>
          <span className={classes.label}>
            {t('#portal.investor.report.balance.list')}:
          </span>
          <Select
            value={version}
            options={versions}
            cls={selectCls}
            onSelect={handleChangeVersion}
            labelField="label"
            valueField="value"
            withButton={true}
            withTranslation={true}
          />
        </ControlWrapper>
        <ControlWrapper>
          <Select
            value={balanceType}
            options={balanceTypesOptions}
            cls={selectCls}
            onSelect={handleChangeBalanceType}
            labelField="label"
            valueField="value"
            withButton
          />
        </ControlWrapper>
        <div className={classes.datePickersWrapper}>
          <ControlWrapper className={classes.controlWrapper}>
            <span className={classes.label}>
              {t('#portal.investor.report.balance.startdate')}:
            </span>
            <div className={classes.datepickerWrapper}>
              <DatePicker
                name="startDate"
                date={startDate}
                handleChange={handleChangeDate}
                maxDate={endDate}
              />
            </div>
          </ControlWrapper>
          <ControlWrapper className={classes.controlWrapper}>
            <span className={classes.label}>
              {t('#portal.investor.report.balance.enddate')}:
            </span>
            <div className={classes.datepickerWrapper}>
              <DatePicker
                name="endDate"
                date={endDate}
                handleChange={handleChangeDate}
                minDate={startDate}
              />
            </div>
          </ControlWrapper>
        </div>
        <ControlWrapper>
          <Download name={fileName} url={`${url}&download=true`} />
        </ControlWrapper>
      </FiltersBar>
      {url ? <HTMLParser url={url} /> : null}
    </>
  );
};

export default memo(InvestotrBalanceDetails);
